import { z } from 'zod';

import { foldDigitsToAscii } from '@shared/format';

import { copy } from './copy';

/**
 * The plan form — three fields, and the third field the catalog names is not one
 * of them: the scope is written by the owning service from configuration, so a
 * control here would ask a question whose answer never varies.
 *
 * **Both numeric fields are digit-tolerant.** An operator on an Arabic keyboard
 * types `٩٠٠٠`, and rejecting that is rejecting the glyph their keyboard
 * produced rather than the number they meant. The fold runs **before**
 * validation, so the message the field shows is about the value and never about
 * the digit set.
 *
 * **The schema validates; it does not convert.** It leaves every field a
 * folded ASCII string, and `toPlanWriteBody` turns those into the integers the
 * wire takes. Keeping the two apart is what lets the form's value type and the
 * form's field type stay the same shape — a schema whose output differed from
 * its input would make every field in the resolver a different type from the
 * `<input>` it drives.
 */

const wholeNumberField = (required: string, positive: string) =>
  z
    .string()
    .trim()
    .transform((value) => foldDigitsToAscii(value))
    .pipe(z.string().min(1, required).regex(/^\d+$/, positive));

export const planFormSchema = z.object({
  name: z.string().trim().min(1, copy.validation.nameRequired).max(255, copy.validation.nameTooLong),

  price: wholeNumberField(copy.validation.priceRequired, copy.validation.pricePositive).refine(
    (value) => Number(value) > 0,
    copy.validation.pricePositive,
  ),

  periodMonths: wholeNumberField(copy.validation.periodRequired, copy.validation.periodPositive).refine(
    (value) => Number(value) >= 1,
    copy.validation.periodPositive,
  ),
});

export type PlanFormValues = z.infer<typeof planFormSchema>;

/** The request body, built from values the schema has already validated. */
export function toPlanWriteBody(values: PlanFormValues) {
  return {
    name: values.name,
    price: Number(values.price),
    period_months: Number(values.periodMonths),
  };
}
